/**
 * Site-wide content: settings and navigation.
 *
 * Loaded once and shared, because the header, footer and every CTA label come from the CMS. A
 * conservative fallback is used while loading or if the request fails, so the chrome always
 * renders something sensible rather than collapsing.
 */
import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { siteApi } from '@/services/api';
import type { Menu, MenuItem, SiteSettings } from '@/services/types';

interface SiteContextValue {
  settings: SiteSettings;
  menus: Record<string, MenuItem[]>;
  isLoading: boolean;
  isError: boolean;
}

export const FALLBACK_SETTINGS: SiteSettings = {
  company_name: 'Shyena Solutions Private Limited',
  company_short_name: 'Shyena Solutions',
  tagline: 'Engineering intelligence for mission-critical operations',
  logo: null,
  logo_dark: null,
  favicon: null,
  registration_number: '',
  primary_email: '',
  sales_email: '',
  careers_email: '',
  support_email: '',
  primary_phone: '',
  secondary_phone: '',
  address_lines: '',
  address_lines_list: [],
  address_single_line: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'India',
  map_embed_url: '',
  linkedin_url: '',
  twitter_url: '',
  youtube_url: '',
  facebook_url: '',
  instagram_url: '',
  github_url: '',
  default_og_image: null,
  footer_about: '',
  copyright_text: 'Shyena Solutions Private Limited. All rights reserved.',
  newsletter_heading: '',
  newsletter_body: '',
  primary_cta_label: 'Explore Solutions',
  primary_cta_url: '/solutions',
  secondary_cta_label: 'Talk to Our Experts',
  secondary_cta_url: '/contact',
  demo_cta_label: 'Request a Demo',
  quote_cta_label: 'Request a Quotation',
  enable_blog: true,
  enable_news: true,
  enable_case_studies: true,
  enable_careers: true,
  enable_newsletter: true,
  maintenance_mode: false,
  maintenance_message: '',
};

const SiteContext = createContext<SiteContextValue>({
  settings: FALLBACK_SETTINGS,
  menus: {},
  isLoading: true,
  isError: false,
});

function indexMenus(menus: Menu[] | undefined): Record<string, MenuItem[]> {
  if (!menus) return {};
  return Object.fromEntries(menus.map((menu) => [menu.key, menu.items]));
}

export function SiteProvider({ children }: { children: ReactNode }) {
  const settingsQuery = useQuery({
    queryKey: ['site', 'settings'],
    queryFn: siteApi.settings,
    staleTime: 10 * 60 * 1000,
  });

  const menusQuery = useQuery({
    queryKey: ['site', 'menus'],
    queryFn: siteApi.menus,
    staleTime: 10 * 60 * 1000,
  });

  const value: SiteContextValue = {
    settings: settingsQuery.data ?? FALLBACK_SETTINGS,
    menus: indexMenus(menusQuery.data),
    isLoading: settingsQuery.isLoading || menusQuery.isLoading,
    isError: settingsQuery.isError || menusQuery.isError,
  };

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSite(): SiteContextValue {
  return useContext(SiteContext);
}

export function useSiteSettings(): SiteSettings {
  return useContext(SiteContext).settings;
}

export function useMenu(key: string): MenuItem[] {
  return useContext(SiteContext).menus[key] ?? [];
}
